import type {
  ContentOutputMetadata,
  ContentUnitOutput,
  ForUnitOutput,
  ImportUnitOutput,
  UseUnitOutput,
} from "@magic-md/core";
import type {
  ExecutionCompletedEvent,
  ExecutionFailedEvent,
  ExecutionStartedEvent,
} from "@magic-md/engine-client/protocol";
import {
  buildContentExecutionEntry,
  buildDefinitionExecutionEntry,
  buildForExecutionEntry,
  buildImportExecutionEntryFromUnitOutput,
  buildSyntheticContentExecutionMeta,
  buildUseExecutionEntry,
  hydrateDefinitionExecutionsFromContentOutput,
  type ExecutionMapSet,
} from "./execution-entry-builders";

export const runningStarts = new Map<string, number>();
export const MINIMUM_RUNNING_MS = 150;

export function getRunningStartTime(unitId: string): number | undefined {
  return runningStarts.get(unitId);
}

export function clearRunningStartTime(unitId: string): void {
  runningStarts.delete(unitId);
}

export function isExecutionCancelledMessage(
  message: string | undefined,
): boolean {
  return message === "Execution cancelled by user";
}

/** Human-readable labels for engine-internal execution phases. */
export function phaseLabel(phase: string): string {
  switch (phase) {
    case "ref-resolve":
      return "Resolving references…";
    case "sidecar-ensure":
      return "Starting backend…";
    case "sidecar-call":
      return "Calling backend…";
    case "action-run":
      return "Running action…";
    case "plugin-progress":
      return "";
    default:
      return phase;
  }
}

export function applyStartedStateForNonDefinitionUnit(
  maps: ExecutionMapSet,
  event: ExecutionStartedEvent,
  startedAt = Date.now(),
): boolean {
  if (event.unitType === "for") {
    const totalIterations = event.totalIterations ?? event.progress?.total ?? 0;
    const prev = maps.forExecutions.get(event.unitId);
    maps.forExecutions.set(event.unitId, {
      forId: event.unitId,
      status: "running",
      totalIterations,
      completedIterations: 0,
      iterations: prev?.iterations ?? [],
      startedAt,
      duration: null,
    });
    return true;
  }

  if (event.unitType === "content") {
    maps.contentExecutions.set(event.unitId, {
      contentId: event.unitId,
      status: "running",
      startedAt,
      duration: null,
    });
    return true;
  }

  if (event.unitType === "import") {
    const prev = maps.importExecutions.get(event.unitId);
    maps.importExecutions.set(event.unitId, {
      importId: event.unitId,
      status: "running",
      startedAt,
      duration: null,
      ...(prev?.symbols ? { symbols: prev.symbols } : {}),
      ...(prev?.document ? { document: prev.document } : {}),
      ...(prev?.meta ? { meta: prev.meta } : {}),
    });
    return true;
  }

  if (event.unitType === "use") {
    maps.useExecutions.set(event.unitId, {
      useId: event.unitId,
      status: "running",
      startedAt,
      duration: null,
    });
    return true;
  }

  return false;
}

export function applyCompletedStateForNonDefinitionUnit(
  maps: ExecutionMapSet,
  event: ExecutionCompletedEvent,
): boolean {
  if (event.unitType === "for") {
    const prev = maps.forExecutions.get(event.unitId);
    maps.forExecutions.set(
      event.unitId,
      buildForExecutionEntry(event.unitId, event.output as ForUnitOutput, {
        startedAt: prev?.startedAt ?? null,
        duration: event.meta.duration,
      }),
    );
    return true;
  }

  if (event.unitType === "content") {
    const prev = maps.contentExecutions.get(event.unitId);
    const output = event.output as ContentUnitOutput;
    const contentMeta = output.__metadata as ContentOutputMetadata | undefined;
    maps.contentExecutions.set(
      event.unitId,
      buildContentExecutionEntry(event.unitId, output.data, {
        startedAt: prev?.startedAt ?? null,
        duration: event.meta.duration,
        meta: contentMeta
          ? {
              total: contentMeta.total,
              succeeded: contentMeta.succeeded,
              failed: contentMeta.failed,
              duration: contentMeta.duration,
              startedAt: contentMeta.startedAt,
            }
          : undefined,
      }),
    );
    return true;
  }

  if (event.unitType === "import") {
    const prev = maps.importExecutions.get(event.unitId);
    maps.importExecutions.set(
      event.unitId,
      buildImportExecutionEntryFromUnitOutput(
        event.unitId,
        event.output as ImportUnitOutput,
        {
          startedAt: prev?.startedAt ?? null,
          duration: event.meta.duration,
        },
      ),
    );
    return true;
  }

  if (event.unitType === "use") {
    const output = event.output as UseUnitOutput;
    const prevUse = maps.useExecutions.get(event.unitId);
    maps.useExecutions.set(
      event.unitId,
      buildUseExecutionEntry(event.unitId, output, {
        startedAt: prevUse?.startedAt ?? null,
        duration: event.meta.duration,
      }),
    );

    if (output.kind === "node" && output.nodeOutput) {
      const prevDefinition = maps.executions.get(event.unitId);
      maps.executions.set(
        event.unitId,
        buildDefinitionExecutionEntry(event.unitId, output.nodeOutput, {
          actionName:
            prevDefinition?.actionName ?? event.meta.actionName ?? null,
          startedAt: prevDefinition?.startedAt ?? prevUse?.startedAt ?? null,
          duration: event.meta.duration,
        }),
      );
    } else if (output.kind === "content" && output.contentOutput) {
      const prevContent = maps.contentExecutions.get(event.unitId);
      maps.contentExecutions.set(
        event.unitId,
        buildContentExecutionEntry(event.unitId, output.contentOutput, {
          startedAt: prevContent?.startedAt ?? prevUse?.startedAt ?? null,
          duration: event.meta.duration,
          meta: buildSyntheticContentExecutionMeta(output.contentOutput, {
            startedAt: event.meta.executedAt,
            duration: event.meta.duration,
          }),
        }),
      );
      hydrateDefinitionExecutionsFromContentOutput(
        maps.executions,
        output.contentOutput,
      );
    }

    return true;
  }

  return false;
}

export function applyFailedStateForNonDefinitionUnit(
  maps: ExecutionMapSet,
  event: ExecutionFailedEvent,
  options: { cancelled?: boolean; now?: number } = {},
): boolean {
  const cancelled = options.cancelled ?? false;
  const now = options.now ?? Date.now();

  if (event.unitType === "for") {
    const prev = maps.forExecutions.get(event.unitId);
    maps.forExecutions.set(event.unitId, {
      forId: event.unitId,
      status: cancelled ? "idle" : "error",
      totalIterations: prev?.totalIterations ?? 0,
      completedIterations: prev?.completedIterations ?? 0,
      iterations: prev?.iterations ?? [],
      startedAt: prev?.startedAt ?? null,
      duration: prev?.startedAt ? now - prev.startedAt : null,
      error: event.error,
    });
    return true;
  }

  if (event.unitType === "content") {
    const prev = maps.contentExecutions.get(event.unitId);
    maps.contentExecutions.set(event.unitId, {
      contentId: event.unitId,
      status: cancelled ? "idle" : "error",
      startedAt: prev?.startedAt ?? null,
      duration: prev?.startedAt ? now - prev.startedAt : null,
      error: event.error,
    });
    return true;
  }

  if (event.unitType === "import") {
    const prev = maps.importExecutions.get(event.unitId);
    maps.importExecutions.set(event.unitId, {
      importId: event.unitId,
      status: cancelled ? "idle" : "error",
      startedAt: prev?.startedAt ?? null,
      duration: prev?.startedAt ? now - prev.startedAt : null,
      error: event.error,
      ...(prev?.symbols ? { symbols: prev.symbols } : {}),
      ...(prev?.document ? { document: prev.document } : {}),
      ...(prev?.meta ? { meta: prev.meta } : {}),
    });
    return true;
  }

  if (event.unitType === "use") {
    const prev = maps.useExecutions.get(event.unitId);
    maps.useExecutions.set(event.unitId, {
      useId: event.unitId,
      status: cancelled ? "idle" : "error",
      startedAt: prev?.startedAt ?? null,
      duration: prev?.startedAt ? now - prev.startedAt : null,
      error: event.error,
    });
    return true;
  }

  return false;
}
